import { CommonModule, NgFor } from '@angular/common';
import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, inject, Input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment-timezone';
import { DsyHeaderCell, DsyRowData, DsyTableConf, DsyTableData } from '../../../../../../shared/models/dsy-table.model';

export interface OfferRowData {
  index: number;
  offer_start_dttm_utc: string;
  offer_end_dttm_utc: string;
  demand: number | string;
  capacity: { min?: number | string; max?: number | string; value?: number | string };
  price: { min?: number | string; max?: number | string; value?: number | string; currency: string };
  baselines?: number | null;
}

@Component({
  selector: 'dr-customer-offers-ui-open-graph-table',
  templateUrl: './open-graph-table.component.html',
  styleUrls: ['./open-graph-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgFor, CommonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class OpenGraphTableComponent implements OnInit, OnChanges {
  private translate = inject(TranslateService);

  @Input() offers!: OfferRowData[];
  @Input() currency!: string;
  @Input() timezone!: string;
  @Input() offerDimensions: string[] = [];
  @Input() showBaseline = false;

  // which dims drive which column, and whether that column is split (↑/↓) or single
  private static readonly DIM_COLUMNS = {
    forecast: { dims: ['DEMAND_FORECAST'], header: 'COM.TABS.OPEN.CHART.FORECAST_HEADER' },
    capacity: { dims: ['kW', 'KW_TO_INCREASE', 'KW_TO_DECREASE'], header: 'COM.TABS.OPEN.CHART.CAPACITY_KW_HEADER' },
    price: { dims: ['PRICE', 'PRICE_TO_INCREASE', 'PRICE_TO_DECREASE'], header: 'COM.TABS.OPEN.CHART.PRICE_HEADER' }
  } as const;

  protected isCapacitySplit = false;
  protected isPriceSplit = false;
  protected hasCapacityIncrease = false;
  protected hasCapacityDecrease = false;
  protected hasPriceIncrease = false;
  protected hasPriceDecrease = false;
  columns: Array<{ key: string; header: string }> = [];
  protected data = signal<DsyTableData>([]);
  config!: DsyTableConf;

  protected formattedTimeZone = signal<string>('');

  ngOnInit(): void {
    this.formattedTimeZone.set(this.formatTimeZone(this.timezone));
    this.refreshTable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['timezone']) {
      this.formattedTimeZone.set(this.formatTimeZone(this.timezone));
    }
    if (changes['offers'] || changes['timezone'] || changes['currency'] || changes['offerDimensions']) {
      this.refreshTable();
    }
  }

  private refreshTable(): void {
    this.columns = this.buildColumns();
    this.updateHeaders();
    this.config = this.buildConf();
    this.buildData();
  }

  private updateHeaders(): void {
    this.columns = this.columns.map((col) => {
      if (col.key === 'index') return col;
      const header = this.translate.instant(col.header);
      if (col.key === 'price') return { ...col, header: `${header} (${this.currency})` };
      return { ...col, header };
    });
  }

  formatInterval(offer: OfferRowData): string {
    const start = moment.utc(offer.offer_start_dttm_utc).tz(this.timezone).format('hh:mm a');
    const end = moment.utc(offer.offer_end_dttm_utc).tz(this.timezone).format('hh:mm a');
    return `${start} - ${end} `;
  }

  private buildConf(): DsyTableConf {
    const rowIds = this.offers?.map((_, i) => `${i}`) ?? [];
    return {
      styling: { table: { areRowsSeparated: true }, rows: { minHeight: '3.7rem', maxHeight: '3.7rem' } },
      renderOrder: {
        columns: { defaultOrder: this.columns.map((c) => c.key) },
        rows: { defaultOrder: [...rowIds] }
      },
      headerRow: {
        id: 'header',
        cells: Object.fromEntries(
          this.columns.map((col) => [
            col.key,
            {
              id: `header-${col.key}`,
              property: col.key,
              value: col.header
            } as DsyHeaderCell
          ])
        ) as Record<string, DsyHeaderCell>
      }
    };
  }

  private buildData(): void {
    if (!this.offers?.length) {
      this.data.set([]);
      return;
    }

    this.data.set(
      this.offers.map((_, index) => {
        const cells: Record<string, { id: string; property: string; value: unknown }> = {};
        this.columns.forEach((col) => {
          cells[col.key] = { id: `cell-${index}-${col.key}`, property: col.key, value: '' };
        });
        return { id: `${index}`, cells } as DsyRowData;
      })
    );
  }

  private formatTimeZone(tz: string): string {
    try {
      return ` (${moment.tz(tz).zoneAbbr()})`;
    } catch {
      return ' (UTC)';
    }
  }

  private has(dim: string): boolean {
    return this.offerDimensions.includes(dim);
  }

  protected hasColumn(key: string): boolean {
    return this.columns.some((c) => c.key === key);
  }

  /** Build the visible column list from offer_dimensions. Always shows index + interval. */
  private buildColumns(): Array<{ key: string; header: string }> {
    const cols: Array<{ key: string; header: string }> = [
      { key: 'index', header: '#' },
      { key: 'timeInterval', header: 'COM.TABS.OPEN.CHART.TIME_INTERVAL_HEADER' }
    ];

    if (this.has('DEMAND_FORECAST')) {
      cols.push({ key: 'forecast', header: OpenGraphTableComponent.DIM_COLUMNS.forecast.header });
    }

    if (this.showBaseline) {
      cols.push({ key: 'baseline', header: 'COM.TABS.BASELINES.TAB_TITLE' });
    }

    // capacity: single (kW) OR split (increase/decrease)
    this.hasCapacityIncrease = this.has('KW_TO_INCREASE');
    this.hasCapacityDecrease = this.has('KW_TO_DECREASE');
    this.isCapacitySplit = this.has('KW_TO_INCREASE') || this.has('KW_TO_DECREASE');
    if (this.has('kW') || this.isCapacitySplit) {
      cols.push({ key: 'capacity', header: OpenGraphTableComponent.DIM_COLUMNS.capacity.header });
    }

    // price: single (PRICE) OR split (increase/decrease)
    this.hasPriceIncrease = this.has('PRICE_TO_INCREASE');
    this.hasPriceDecrease = this.has('PRICE_TO_DECREASE');
    this.isPriceSplit = this.has('PRICE_TO_INCREASE') || this.has('PRICE_TO_DECREASE');
    if (this.has('PRICE') || this.isPriceSplit) {
      cols.push({ key: 'price', header: OpenGraphTableComponent.DIM_COLUMNS.price.header });
    }

    return cols;
  }
}
